/* eslint-disable @typescript-eslint/no-unused-vars */
import KNB from '@wmfe/mach-pro-knb';
import { getLocale } from '@/src/utils/getLocale';
import { reportRaptorMetrics } from '@/src/common/SLRaptor';

export class IMChat {
  // API来源
  dx_apisource = 'mrn';
  // 业务申请的业务线标识
  channelId = 1056;
  // 消息回调映射 {uuid: { callback: , isReceipt: 0 }}
  callbackInfo: any = {};
  // 当前的消息id
  lastMessageId = '';
  // 注册消息监听重试次数
  retryCount = 0;

  generateUUID() {
    const lut = [] as any;
    for (let i = 0; i < 256; i++) {
      lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
    }

    const d0 = Math.random() * 0xffffffff | 0;
    const d1 = Math.random() * 0xffffffff | 0;
    const d2 = Math.random() * 0xffffffff | 0;
    const d3 = Math.random() * 0xffffffff | 0;

    const uuid = `${lut[d0 & 0xff] + lut[d0 >> 8 & 0xff] + lut[d0 >> 16 & 0xff] + lut[d0 >> 24 & 0xff]}-${
      lut[d1 & 0xff]}${lut[d1 >> 8 & 0xff]}-${lut[d1 >> 16 & 0x0f | 0x40]}${lut[d1 >> 24 & 0xff]}-${
      lut[d2 & 0x3f | 0x80]}${lut[d2 >> 8 & 0xff]}-${lut[d2 >> 16 & 0xff]}${lut[d2 >> 24 & 0xff]
    }${lut[d3 & 0xff]}${lut[d3 >> 8 & 0xff]}${lut[d3 >> 16 & 0xff]}${lut[d3 >> 24 & 0xff]}`;

    return uuid;
  }

  /**
     * 发送IM消息
     * @param payload 入参
     * @param callback 回调
     */
  sendMessage = (payload, callback) => {
    const message = this.constructorIMMessage(payload);
    const { uuid = '' } = message || {};
    KNB.use('dxsdk.sendMessage', {
      action: 'dxsdk.sendMessage', // 事件名
      apiSource: this.dx_apisource, // API来源mrn或picasso
      ...message,
      success: (res) => {
        this.callbackInfo[uuid] = { callback, isReceipt: false };
        this.lastMessageId = uuid;
        this.reportCallKnbApiSuccessRate(true, { action: 'dxsdk.sendMessage', type: 'use' });
      },
      fail: (error) => {
        if (callback) {
          callback({ success: 0 });
        }
        this.reportCallKnbApiSuccessRate(false, { action: 'dxsdk.sendMessage', type: 'use' });
      }
    });
  }

  /**
     * 订阅消息发送消息回执
     */
  subscribeIMSendMessage = () => {
    KNB.use('dxsdk.registerDXSDKEvent', {
      action: 'dxsdk.messagesStatusChange', // 事件名
      apiSource: this.dx_apisource, // mrn或picasso
      channelId: this.channelId, // 业务线标识
      success: (res) => {
        KNB.subscribe({
          action: 'dxsdk.messagesStatusChange', // 事件名
          handle: (data) => { // 消息发送成功后会有多次回调
            const { detail } = data || {};
            const { messages } = detail || {};
            if (Array.isArray(messages)) {
              const currentMessage = messages?.[0] || {};
              const { uuid, status } = currentMessage;
              const callbackInfo = this.callbackInfo[uuid];
              const { isReceipt, callback } = callbackInfo || {};
              if (!isReceipt) {
                // 消息发送成功或明确发送失败给予回调
                const shouldCallback = !!((status === 2 || status === 3));
                if (shouldCallback && callback) {
                  this.callbackInfo[uuid] = { callback, isReceipt: true };
                  const isSuccess = status === 2;
                  callback({ success: isSuccess });
                }
                if (shouldCallback) {
                  // 上报发送消息成功率
                  this.reportSendMessageSuccessRate(status === 2);
                }
              }
            }
          },
          success: (data) => {
            this.reportCallKnbApiSuccessRate(true, { action: 'dxsdk.messagesStatusChange', type: 'subscribe' });
          },
          fail: (error) => {
            const callbackInfo = this.callbackInfo[this.lastMessageId];
            const { isReceipt, callback } = callbackInfo || {};
            if (!isReceipt && callback) {
              callback({ success: false });
            }
            this.reportCallKnbApiSuccessRate(false, { action: 'dxsdk.messagesStatusChange', type: 'subscribe' });
          }
        });
        this.reportCallKnbApiSuccessRate(true, { action: 'dxsdk.messagesStatusChange', type: 'use' });
      },
      fail: (error) => {
        this.retryCount++;
        // 注册失败后重试一次
        if (this.retryCount < 2) {
          this.subscribeIMSendMessage();
        }
        this.reportCallKnbApiSuccessRate(false, { action: 'dxsdk.messagesStatusChange', type: 'use' });
      }
    });
  }

  /**
     * 取消订阅
     */
  unsubscribeIMSendMessage = () => {
    KNB.unsubscribe({
      action: 'dxsdk.messagesStatusChange', // 事件名
      success: (data) => {
        // console.log('KNB unsubscribe custom_action success');
        this.reportCallKnbApiSuccessRate(true, { action: 'dxsdk.messagesStatusChange', type: 'unsubscribe' });
      },
      fail: () => {
        // console.log('KNB unsubscribe custom_action fail');
        this.reportCallKnbApiSuccessRate(false, { action: 'dxsdk.messagesStatusChange', type: 'unsubscribe' });
      }
    });
  }

  /**
     * 构造IM消息体
     * @param payload
     * @returns
     */
  constructorIMMessage = (payload) => {
    // 消息唯一id
    const uuid = this.generateUUID();
    const locale = getLocale();
    const {
      text, userDxUid, courierDxUid, orderViewId
    } = payload || {};
    const compass = Mach.requireModule('SLCompass');
    const compassInfo = compass?.getCompassInfo?.() || {};
    const region = compassInfo?.region;
    const cityId = compassInfo?.cityId;

    return {
      sessionType: 'im-peer-custom', // 会话类型单聊
      toAppId: 73, // 骑手端appId
      channelId: this.channelId, // 业务申请的业务线标识
      dataType: 0, // 普通消息
      type: 1, // 消息类型
      uuid, // 消息唯一标识
      from: userDxUid, // 消息发送方uid
      to: courierDxUid, // 消息的接收方信息（对方uid、群组uid、公众号uid）
      cts: new Date().getTime(), // 消息发送时间
      belongTo: courierDxUid, // 会话的标识（对方uid、群组uid、公众号uid）
      deviceType: 1, // 发型消息的设备类型 1表示是手机
      body: {
        text,
      },
      sid: orderViewId, // 自定义会话才有值，表示业务自定义会话的标识
      extension: JSON.stringify({ // 扩展字段，用于给业务方放置业务数据
        systemMessageType: 1,
        orderViewId,
        locale,
        i18nInfo: {
          locale,
          region,
          cityId
        }
      }),
    };
  }

  /**
     * 上报使用KNB API成功率
     */
  reportCallKnbApiSuccessRate = (isSuccess: boolean, tags = {}) => {
    reportRaptorMetrics({ CallKNBApiSuccessRate: isSuccess ? 1 : 0 }, tags, 1.0);
  }

  /**
     * 上报发送消息成功率
     */
  reportSendMessageSuccessRate = (isSuccess: boolean, tags = {}) => {
    reportRaptorMetrics({ SendMessageSuccessRate: isSuccess ? 1 : 0 }, tags, 1.0);
  }
}
